import { type SupportedLocale } from "@/i18n";
import { api } from "@/convex/_generated/api";
import { type LocaleAlternate, type RouteKey } from "@/lib/routes";
import {
  absoluteUrl,
  composeMetaDescription,
  composeSeoTitle,
  hreflangAlternates,
  ogLocale,
  SITE_NAME,
} from "@/lib/seo";
import { staticSocialImage } from "@/lib/static-seo";
import {
  readInitialPublicBranding,
  resolvePublicBranding,
} from "@/lib/public-branding";
import { twitterSiteFromUrl } from "@/lib/social";
import { useDeferredPublicEnhancement } from "@/hooks/use-deferred-public-enhancement";
import { useQuery } from "convex/react";
import { useEffect, useMemo } from "react";

type JsonLd = Record<string, unknown>;

type SeoProps = {
  title: string;
  description?: string;
  /** Canonical path without domain, e.g. "/it/servizi" */
  path?: string;
  /**
   * Absolute canonical URL. Overrides `path` when the canonical lives on another
   * domain (e.g. an aggregated article whose original is hosted elsewhere).
   */
  canonicalUrl?: string;
  /** Current locale, used for og:locale and <html lang>. */
  locale?: SupportedLocale;
  /** Localized permalinks of this page for hreflang alternate tags. */
  alternates?: LocaleAlternate[];
  /** Stable static-page key used for page-specific social artwork. */
  routeKey?: RouteKey;
  /** Absolute image URL for social sharing. Falls back to the site default. */
  image?: string;
  /** Accessible description of the social image. Defaults to the page title. */
  imageAlt?: string;
  /** OpenGraph type. Defaults to "website". */
  type?: "website" | "article";
  /** Set true to keep the page out of search indexes. */
  noindex?: boolean;
  /** Keep links crawlable on a noindex page (for transparent source previews). */
  followWhenNoindex?: boolean;
  /** JSON-LD structured data object(s) for this page. */
  jsonLd?: JsonLd | JsonLd[];
  /** RSS/Atom discovery link for editorial sections. */
  feed?: { title: string; href: string };
  /** Atom discovery link for editorial sections. */
  atomFeed?: { title: string; href: string };
  /** Likely next documents to fetch only when the browser is idle. */
  prefetch?: string[];
};

const MANAGED_ATTR = "data-seo-managed";
const DEFAULT_SOCIAL_IMAGE = absoluteUrl("/social/home.png?v=0eaea55852ed8fdf");

function setMeta(attr: "name" | "property", key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function setLinkCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function removeLinkCanonical() {
  document.head.querySelector('link[rel="canonical"]')?.remove();
}

// Remove any alternate/JSON-LD nodes we added on a previous render so the head
// never accumulates stale tags when navigating between pages.
function clearManaged(selector: string) {
  document.head
    .querySelectorAll(`${selector}[${MANAGED_ATTR}]`)
    .forEach((el) => el.remove());
}

function clearServerSeo(selector: string) {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
}

export default function Seo({
  title,
  description,
  path,
  canonicalUrl,
  locale,
  alternates,
  routeKey,
  image,
  imageAlt,
  type = "website",
  noindex = false,
  followWhenNoindex = false,
  jsonLd,
  feed,
  atomFeed,
  prefetch = [],
}: SeoProps) {
  const enhancementsReady = useDeferredPublicEnhancement();
  const initialBranding = useMemo(() => readInitialPublicBranding(), []);
  const liveBranding = useQuery(
    api.crm.branding.getPublic,
    enhancementsReady && !initialBranding ? {} : "skip",
  );
  const branding = resolvePublicBranding(initialBranding, liveBranding);
  const twitterSite = twitterSiteFromUrl(branding?.xUrl);
  const safeTitle = composeSeoTitle(title, "", 60);
  const safeDescription = description
    ? composeMetaDescription(description, "", 1, 160)
    : undefined;
  useEffect(() => {
    document.title = safeTitle;
    setMeta("property", "og:title", safeTitle);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", type);
    for (const key of [
      "twitter:card",
      "twitter:title",
      "twitter:description",
      "twitter:image",
      "twitter:image:alt",
      "twitter:site",
    ]) {
      removeMeta("name", key);
    }
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", safeTitle);

    if (safeDescription) {
      setMeta("name", "description", safeDescription);
      setMeta("property", "og:description", safeDescription);
      setMeta("name", "twitter:description", safeDescription);
    } else {
      removeMeta("name", "description");
      removeMeta("property", "og:description");
      removeMeta("name", "twitter:description");
    }

    // meta keywords has no effect on Google indexing and is intentionally
    // removed, including stale server markup left by an older deployment.
    removeMeta("name", "keywords");

    if (locale) {
      setMeta("property", "og:locale", ogLocale(locale));
    }

    // robots
    setMeta(
      "name",
      "robots",
      noindex
        ? followWhenNoindex
          ? "noindex, follow, max-image-preview:large"
          : "noindex, nofollow"
        : "index, follow, max-image-preview:large",
    );

    const socialImage =
      image ?? (routeKey ? staticSocialImage(routeKey) : DEFAULT_SOCIAL_IMAGE);
    const socialImageAlt = imageAlt ?? safeTitle;
    setMeta("property", "og:image", socialImage);
    setMeta("property", "og:image:secure_url", socialImage);
    setMeta("property", "og:image:alt", socialImageAlt);
    setMeta("name", "twitter:image", socialImage);
    setMeta("name", "twitter:image:alt", socialImageAlt);
    if (twitterSite) {
      setMeta("name", "twitter:site", twitterSite);
    } else {
      removeMeta("name", "twitter:site");
    }
    if (!image) {
      setMeta("property", "og:image:type", "image/png");
      setMeta("property", "og:image:width", "1200");
      setMeta("property", "og:image:height", "630");
    } else {
      removeMeta("property", "og:image:type");
      removeMeta("property", "og:image:width");
      removeMeta("property", "og:image:height");
    }

    // Canonical + og:url. When an absolute canonicalUrl is given (e.g. an
    // aggregated article whose original lives on another domain), point the
    // canonical there; otherwise use the internal path on the live origin.
    // og:url always reflects the actual page URL being viewed.
    if (path) {
      const url = absoluteUrl(path);
      const canonical = canonicalUrl ?? url;
      setLinkCanonical(canonical);
      setMeta("property", "og:url", canonical);
    } else if (canonicalUrl) {
      setLinkCanonical(canonicalUrl);
      setMeta("property", "og:url", canonicalUrl);
    } else {
      removeLinkCanonical();
      removeMeta("property", "og:url");
    }

    // hreflang alternates (absolute, production domain).
    clearManaged('link[rel="alternate"]');
    clearServerSeo(
      'link[rel="alternate"][hreflang], link[rel="alternate"][type="application/rss+xml"], link[rel="alternate"][type="application/atom+xml"]',
    );
    if (alternates && alternates.length > 0) {
      for (const tag of hreflangAlternates(
        alternates,
        routeKey === "home" ? "/it" : undefined,
      )) {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", tag.hreflang);
        link.setAttribute("href", tag.href);
        link.setAttribute(MANAGED_ATTR, "");
        document.head.appendChild(link);
      }
    }
    if (feed) {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("type", "application/rss+xml");
      link.setAttribute("title", feed.title);
      link.setAttribute("href", feed.href);
      link.setAttribute(MANAGED_ATTR, "");
      document.head.appendChild(link);
    }
    if (atomFeed) {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("type", "application/atom+xml");
      link.setAttribute("title", atomFeed.title);
      link.setAttribute("href", atomFeed.href);
      link.setAttribute(MANAGED_ATTR, "");
      document.head.appendChild(link);
    }

    clearManaged('link[rel="prefetch"]');
    clearServerSeo('link[rel="prefetch"][as="document"]');
    for (const href of prefetch) {
      const link = document.createElement("link");
      link.setAttribute("rel", "prefetch");
      link.setAttribute("as", "document");
      link.setAttribute("href", href);
      link.setAttribute(MANAGED_ATTR, "");
      document.head.appendChild(link);
    }

    // JSON-LD structured data.
    clearManaged('script[type="application/ld+json"]');
    clearServerSeo('script[type="application/ld+json"]');
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const block of blocks) {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute(MANAGED_ATTR, "");
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
      }
    }

    return () => {
      clearManaged('link[rel="alternate"]');
      clearManaged('link[rel="prefetch"]');
      clearManaged('script[type="application/ld+json"]');
    };
  }, [
    safeTitle,
    safeDescription,
    path,
    canonicalUrl,
    locale,
    alternates,
    routeKey,
    image,
    imageAlt,
    type,
    noindex,
    followWhenNoindex,
    jsonLd,
    feed,
    atomFeed,
    twitterSite,
    prefetch,
  ]);

  return null;
}
